import { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  styled,
  TableHead as MuiTableHead,
  TableRow,
  TableSortLabel,
  tableSortLabelClasses,
  Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';

import { TableCell } from './TableCell';
import { TableHeadProps } from './types';
import { getDefaultColumnOrder } from './utils';

const StickyTableHead = styled(MuiTableHead)`
  position: sticky;
  top: 0;
  border: none;
  box-shadow: none;
  z-index: 1;
`;

const StyledTableCell = styled(TableCell)`
  &:first-of-type {
    padding-left: inherit;
  }

  &:last-of-type {
    padding-right: inherit;
  }
`;

const StyledTableSortLabel = styled(TableSortLabel)(({ theme }) => ({
  display: 'flex',
  lineHeight: 1.5,
  margin: theme.spacing(-0.75, -1),
  padding: theme.spacing(0.75, 1),
  justifyContent: 'space-between',
  '&:focus-visible, &:hover': {
    color: theme.palette.text.primary,
  },
  '&:hover': {
    background: theme.palette.action.hover,
  },
  '&:focus-visible': {
    background: theme.palette.action.focus,
    outline: '2px solid',
    outlineOffset: -2,
    [`.${tableSortLabelClasses.icon}`]: {
      opacity: 0.5,
    },
  },
  [`&.${tableSortLabelClasses.active}`]: {
    background: theme.palette.action.selected,
    [`.${tableSortLabelClasses.icon}`]: {
      opacity: 1,
      color: theme.palette.text.primary,
    },
  },
  [`.${tableSortLabelClasses.icon}`]: {
    marginRight: -2,
  },
}));

export function TableHead<T>({
  columns,
  hasActions,
  loading,
  onRequestSort,
  order,
  orderBy,
  selectable,
}: TableHeadProps<T>) {
  const { t } = useTranslation();

  const createSortHandler = (property: string) => (event: MouseEvent<unknown>) =>
    onRequestSort(event, property);

  return (
    <Paper component={StickyTableHead} elevation={1} square>
      <TableRow>
        {selectable && <StyledTableCell padding='checkbox' variant='head' />}

        {columns?.map(column => {
          const { key, disableSortable, label, TableCellProps: tableCellProps } = column;

          return (
            <StyledTableCell
              key={key}
              sortDirection={orderBy === key ? order : false}
              variant='head'
              {...{ ...tableCellProps, ...(column.isTypeColumn ? { minimalWidth: true } : {}) }}
            >
              {loading || disableSortable ? (
                label
              ) : (
                <StyledTableSortLabel
                  active={orderBy === key}
                  direction={orderBy === key ? order : getDefaultColumnOrder(column)}
                  focusRipple
                  LinkComponent={Paper}
                  onClick={createSortHandler(key)}
                >
                  {label}
                  {orderBy === key ? (
                    <Box component='span' sx={visuallyHidden}>
                      {t('sorted', { context: order })}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              )}
            </StyledTableCell>
          );
        })}

        {hasActions && (
          <TableCell variant='head'>
            <Typography component='span' sx={visuallyHidden}>
              {t('actions')}
            </Typography>
          </TableCell>
        )}
      </TableRow>
    </Paper>
  );
}
